/**
 * EL PRESUPUESTO DEL COBRO (12/10/2026)
 * ------------------------------------
 * El pago del resto de una cirugía tiene que colgar de su presupuesto. Si se
 * apunta suelto desde Cobros, la ficha o el Datáfono, el presupuesto nunca
 * sale cubierto: ni en su diálogo ni en la agenda, donde la cirugía con la
 * reserva pagada se queda en azul para siempre (no pasa a verde).
 *
 * Con el paciente elegido, si tiene presupuestos ACEPTADOS con importe
 * pendiente, sale el campo «Presupuesto» («Nº X — faltan Y €» o «Ninguno»):
 *   · en un cobro de cirugía (`isCirugiaConcept`), elegido el primero de la
 *     lista: desde el 3/11/2026 el servidor pone primero el presupuesto de
 *     la CITA DE CIRUGÍA del paciente (`de_la_cita`, con `cita_fecha`), no el
 *     más reciente, que con dos presupuestos podía ser el de otra
 *     intervención (administración; services/saldo_por_cita). Sin cita que
 *     lo diga, el más reciente como siempre;
 *   · en los demás (consulta, revisión…), sin elegir: «Ninguno»;
 *   · en una devolución no sale (el servidor decide de dónde sale, como hoy);
 *   · en una reserva tampoco: la reserva sella su presupuesto como siempre
 *     (el servidor), y elegir uno aquí sellaría el que no es;
 *   · lo que elija la persona manda y no se cambia solo al tocar el concepto.
 * Al cambiar de paciente la lista se vacía en el momento: nunca se ve, ni se
 * manda, un presupuesto del paciente anterior mientras llega la nueva.
 * El servidor comprueba que el presupuesto es de ese paciente
 * (`cobros_router._presupuesto_del_cobro`).
 */
import React, { useEffect, useState } from 'react';
import { api, formatEUR } from '../../lib/api';
import { isCirugiaConcept, isDevolucionConcept, isReservaConcept } from '../../lib/cobroPresets';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export const SIN_PRESUPUESTO = '__ninguno__';

/**
 * Una fecha de cita «AAAA-MM-DD» como se lee: «dd/mm/aaaa». Por texto, sin
 * pasar por Date: una fecha sin hora no tiene que moverse de día con la zona
 * horaria. Lo que no sea una fecha así sale tal cual.
 */
export function fechaDeCita(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(ymd || '');
}

/**
 * De dónde sale el presupuesto del «Saldo pendiente» (administración,
 * 3/11/2026): el servidor lo elige por la cita de cirugía del paciente
 * (`presupuesto_por`: reserva | detalles | unico | reciente) y aquí se dice
 * con palabras, para que con dos presupuestos se sepa cuál se está mirando.
 */
export function origenDelPresupuesto(summary) {
  const por = summary?.presupuesto_por;
  const fecha = summary?.cita_referencia?.date ? fechaDeCita(summary.cita_referencia.date) : '';
  if ((por === 'reserva' || por === 'detalles') && fecha) return `Presupuesto de la cirugía del ${fecha}`;
  if (por === 'unico') return 'Único presupuesto aceptado con pendiente';
  if (por === 'reciente') return 'Presupuesto más reciente (sin cita de cirugía que lo diga)';
  return '';
}

/** Lo que se lee de un presupuesto pendiente en el desplegable. El de la cita
 *  de cirugía lo dice: «· cirugía dd/mm/aaaa» (3/11/2026). */
export function textoPresupuesto(p) {
  const numero = p.number ? `Nº ${p.number}` : 'Presupuesto';
  const que = p.surgery_name ? ` (${p.surgery_name})` : '';
  const cita = p.de_la_cita && p.cita_fecha ? ` · cirugía ${fechaDeCita(p.cita_fecha)}` : '';
  return `${numero}${que} — faltan ${formatEUR(p.pendiente)}${cita}`;
}

/**
 * Estado del campo para un paciente y un concepto. Devuelve la lista, el
 * presupuesto que va en el cobro (`budgetId`, '' = ninguno), el setter de lo
 * que elija la persona y si el campo se enseña.
 */
export function usePresupuestoDelCobro(patientId, concept) {
  const [lista, setLista] = useState([]);
  // null = la persona no ha tocado el campo: manda lo de por defecto.
  const [elegido, setElegido] = useState(null);
  useEffect(() => {
    setElegido(null);
    setLista([]);
    if (!patientId) return undefined;
    let vigente = true;
    api.get('/api/cobros/presupuestos-pendientes', { params: { patient_id: patientId } })
      .then((r) => { if (vigente) setLista(Array.isArray(r.data) ? r.data : []); })
      // Sin la lista, el cobro se apunta como hasta hoy (sin presupuesto).
      .catch(() => { if (vigente) setLista([]); });
    return () => { vigente = false; };
  }, [patientId]);
  const visible = lista.length > 0 && !isDevolucionConcept(concept) && !isReservaConcept(concept);
  const porDefecto = isCirugiaConcept(concept) && lista[0] ? lista[0].id : '';
  const budgetId = visible ? (elegido ?? porDefecto) : '';
  return { lista, budgetId, setBudgetId: setElegido, visible };
}

/** El desplegable «Presupuesto». */
export function PresupuestoDelCobroField({ estado, dataTestid = 'cobro-presupuesto', className = '' }) {
  if (!estado.visible) return null;
  return (
    <div className={className} data-testid={dataTestid}>
      <Label>Presupuesto</Label>
      <Select
        value={estado.budgetId || SIN_PRESUPUESTO}
        onValueChange={(v) => estado.setBudgetId(v === SIN_PRESUPUESTO ? '' : v)}
      >
        <SelectTrigger className="mt-1.5" data-testid={`${dataTestid}-select`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={SIN_PRESUPUESTO}>Ninguno</SelectItem>
          {estado.lista.map((p) => (
            <SelectItem key={p.id} value={p.id}>{textoPresupuesto(p)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="text-xs text-muted-foreground mt-1">
        El pago del resto de una cirugía va contra su presupuesto: así se sabe cuánto falta.
      </div>
    </div>
  );
}
